import { Component } from '@theme/component';

/**
 * Layer component that displays content in a sliding dialog from the right.
 * Extends the base Component class to manage layer open/close state.
 *
 * @extends {Component}
 */
class LayerComponent extends Component {
  /**
   * Required refs that must be present in the component.
   * @type {string[]}
   */
  requiredRefs = ['dialog', 'trigger'];

  /**
   * Flag to prevent multiple close animations.
   * @type {boolean}
   */
  #isClosing = false;

  /**
   * Saved original close method.
   * @type {(() => void) | null}
   */
  #originalClose = null;

  /**
   * Position of the layer (left or right).
   * @type {string}
   */
  #position = 'right';

  /**
   * Called when the element is connected to the document's DOM.
   */
  connectedCallback() {
    this.#position = this.dataset.position || 'right';
    super.connectedCallback();
    this.#setupEventListeners();
  }

  #setupEventListeners() {
    if (!this.dialogElement) return;

    // Save original close method
    this.#originalClose = this.dialogElement.close.bind(this.dialogElement);

    // Set initial transform - start hidden off-screen
    const initialTransform = this.#position === 'right' ? 'translateX(100%)' : 'translateX(-100%)';
    this.dialogElement.style.transform = initialTransform;

    // Observer to detect when dialog opens
    const observer = new MutationObserver(() => {
      if (!this.dialogElement) return;

      if (this.dialogElement.hasAttribute('open')) {
        // Opening
        this.#isClosing = false;
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this.#handleOpen();
          });
        });
      } else if (!this.#isClosing) {
        // Closing (only if not already closing)
        this.#handleClose();
      }
    });

    observer.observe(this.dialogElement, {
      attributes: true,
      attributeFilter: ['open']
    });

    // Intercept showModal to set initial transform
    const originalShowModal = this.dialogElement.showModal.bind(this.dialogElement);
    this.dialogElement.showModal = () => {
      if (!this.dialogElement) return;
      const initialTransform = this.#position === 'right' ? 'translateX(100%)' : 'translateX(-100%)';
      this.dialogElement.style.transform = initialTransform;
      originalShowModal();
      // Animation will be triggered by MutationObserver
    };

    // Intercept close to animate before closing
    this.dialogElement.close = () => {
      if (!this.dialogElement || !this.#originalClose) return;
      if (this.dialogElement.hasAttribute('open') && !this.#isClosing) {
        this.#animateClose(() => {
          if (this.#originalClose) {
            this.#originalClose();
          }
        });
      } else {
        this.#originalClose();
      }
    };

    // Close dialog when clicking on backdrop
    this.dialogElement.addEventListener('click', (event) => {
      if (event.target === this.dialogElement) {
        this.dialogElement.close();
      }
    });
  }

  #handleOpen() {
    if (!this.dialogElement) return;

    this.triggerElement.setAttribute('aria-expanded', 'true');

    // Ensure starting position
    const initialTransform = this.#position === 'right' ? 'translateX(100%)' : 'translateX(-100%)';
    this.dialogElement.style.transform = initialTransform;

    // Force reflow
    void this.dialogElement.offsetHeight;

    // Use double requestAnimationFrame to ensure browser has applied initial state
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (!this.dialogElement) return;
        // Animate to visible position
        this.dialogElement.style.transform = 'translateX(0)';
        this.dialogElement.classList.add('is-open');
      });
    });
  }

  #handleClose() {
    if (!this.dialogElement) return;

    this.triggerElement.setAttribute('aria-expanded', 'false');
    this.dialogElement.classList.remove('is-open');
    const hiddenTransform = this.#position === 'right' ? 'translateX(100%)' : 'translateX(-100%)';
    this.dialogElement.style.transform = hiddenTransform;
  }

  /**
   * @param {() => void} callback
   */
  #animateClose(callback) {
    if (!this.dialogElement) return;

    this.#isClosing = true;
    this.dialogElement.classList.remove('is-open');

    // Animate out in the correct direction
    const hiddenTransform = this.#position === 'right' ? 'translateX(100%)' : 'translateX(-100%)';
    this.dialogElement.style.transform = hiddenTransform;

    // Wait for animation to complete
    setTimeout(() => {
      callback();
      this.#isClosing = false;
    }, 300);
  }

  /**
   * Opens the layer dialog.
   * @public
   */
  open() {
    if (!this.dialogElement.open) {
      this.dialogElement.showModal();
    }
  }

  /**
   * Closes the layer dialog.
   * @public
   */
  close() {
    if (this.dialogElement.open) {
      this.dialogElement.close();
    }
  }

  /**
   * Gets the dialog element.
   * @returns {HTMLDialogElement}
   */
  get dialogElement() {
    return /** @type {HTMLDialogElement} */ (this.refs.dialog);
  }

  /**
   * Gets the trigger button element.
   * @returns {HTMLButtonElement}
   */
  get triggerElement() {
    return /** @type {HTMLButtonElement} */ (this.refs.trigger);
  }
}

customElements.define('layer-component', LayerComponent);
